import React, { useState } from 'react';
import { BookOpen, Check, SkipForward, Plus } from 'lucide-react';
import { getISOSuggestions } from '../../services/speechToISOService';

/**
 * VocabularyLearner — Inline learning component
 * 
 * Appears when transcription contains words not matched by the vocabulary.
 * Shows suggestions per unmatched word, lets the monteur pick the right
 * ISO mapping (or type a custom one), then saves it for future recognition.
 * 
 * Non-blocking: monteur can skip at any time.
 */
export default function VocabularyLearner({
    unmatchedWords,
    vocabulary,
    onLearn,  // (word, category, isoValue) => void
    onDismiss
}) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('failures');
    const [customValue, setCustomValue] = useState('');
    const [showCustom, setShowCustom] = useState(false);
    const [learnedCount, setLearnedCount] = useState(0);

    if (!unmatchedWords || unmatchedWords.length === 0) return null;

    const currentWord = unmatchedWords[currentIndex];
    const isLastWord = currentIndex >= unmatchedWords.length - 1;
    const suggestions = getISOSuggestions(selectedCategory, vocabulary);

    // ISO translation map (same as CompletionPanel)
    const isoTranslationMap = {
        "Vibration": "Trillingen", "Noise": "Geluidshinder", "Leakage": "Lekkage",
        "Seized": "Vastgelopen", "Wear": "Slijtage", "Corrosion": "Corrosie",
        "Lubrication": "Smering", "Misalignment": "Uitlijning", "Overload": "Overbelasting",
        "Bearing": "Lager", "Seal": "Afdichting", "Pump": "Pomp", "Motor": "Motor",
        "Unknown": "Onbekend"
    };

    const categoryLabels = {
        failures: { label: 'Failure Mode', icon: '💥', color: '#ef4444' },
        causes: { label: 'Oorzaak', icon: '🔍', color: '#f59e0b' },
        components: { label: 'Component', icon: '🧩', color: '#3b82f6' }
    };

    const handleSelect = (isoValue) => {
        onLearn(currentWord, selectedCategory, isoValue);
        setLearnedCount(prev => prev + 1);
        advance();
    };

    const handleCustomSubmit = () => {
        if (customValue.trim()) {
            onLearn(currentWord, selectedCategory, customValue.trim());
            setLearnedCount(prev => prev + 1);
            setCustomValue('');
            setShowCustom(false);
            advance();
        }
    };

    const advance = () => {
        setShowCustom(false);
        setCustomValue('');
        if (isLastWord) {
            onDismiss?.();
        } else {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const skip = () => advance();
    const dismissAll = () => onDismiss?.();

    const catInfo = categoryLabels[selectedCategory];

    return (
        <div className="mt-4 rounded-xl border-2 border-amber-500/30 bg-amber-500/5 p-4 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <BookOpen size={16} style={{ color: '#f59e0b' }} />
                    <span className="text-sm font-black text-white uppercase tracking-tight">
                        Zelflerende Dictionary
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                        {currentIndex + 1}/{unmatchedWords.length}
                    </span>
                </div>
                <button
                    onClick={dismissAll}
                    className="text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-wider transition-colors"
                >
                    Alles overslaan
                </button>
            </div>

            {/* Current word */}
            <div className="bg-slate-800/80 rounded-lg p-4 border border-slate-700 mb-3">
                <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">
                    Onbekend woord
                </div>
                <div className="text-xl font-black text-white">
                    "{currentWord}"
                </div>
                <div className="text-[10px] text-slate-400 mt-1 font-medium">
                    Dit woord werd niet herkend. Wat bedoelde u?
                </div>
            </div>

            {/* Category selector */}
            <div className="flex gap-2 mb-3">
                {Object.entries(categoryLabels).map(([key, info]) => (
                    <button
                        key={key}
                        onClick={() => { setSelectedCategory(key); setShowCustom(false); }}
                        className={`flex-1 py-2 px-3 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all border-2 ${selectedCategory === key
                                ? 'border-white/30 text-white scale-[1.02] shadow-lg'
                                : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300'
                            }`}
                        style={selectedCategory === key ? { backgroundColor: info.color + '30' } : {}}
                    >
                        <span className="mr-1">{info.icon}</span> {info.label}
                    </button>
                ))}
            </div>

            {/* Suggestions grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                {suggestions.map((value) => (
                    <button
                        key={value}
                        onClick={() => handleSelect(value)}
                        className="py-2.5 px-3 rounded-lg bg-slate-800/80 border border-slate-700 text-sm font-bold text-white hover:border-green-500/50 hover:bg-green-500/10 transition-all text-left flex items-center gap-2 active:scale-95"
                    >
                        <Check size={12} className="text-slate-600 shrink-0" />
                        <span className="truncate">
                            {isoTranslationMap[value] || value}
                        </span>
                    </button>
                ))}
            </div>

            {/* Custom input */}
            {!showCustom ? (
                <button
                    onClick={() => setShowCustom(true)}
                    className="w-full py-2 rounded-lg border-2 border-dashed border-slate-700 text-slate-500 text-xs font-bold uppercase hover:border-slate-500 hover:text-slate-300 transition-all flex items-center justify-center gap-2"
                >
                    <Plus size={14} /> Anders (zelf invullen)
                </button>
            ) : (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={customValue}
                        onChange={(e) => setCustomValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCustomSubmit();
                            e.stopPropagation();
                        }}
                        placeholder="Typ ISO waarde..."
                        autoFocus
                        className="flex-1 bg-slate-900 border-2 border-amber-500/50 rounded-lg px-3 py-2 text-sm text-white font-bold focus:border-amber-400 focus:outline-none"
                    />
                    <button
                        onClick={handleCustomSubmit}
                        disabled={!customValue.trim()}
                        className="px-4 py-2 rounded-lg bg-green-600 text-white font-black text-xs uppercase disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 transition-all"
                    >
                        Opslaan
                    </button>
                </div>
            )}

            {/* Skip button */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700/50">
                <div className="text-[10px] text-slate-600 font-medium">
                    {learnedCount > 0 && (
                        <span className="text-green-400">✓ {learnedCount} woord{learnedCount !== 1 ? 'en' : ''} geleerd</span>
                    )}
                </div>
                <button
                    onClick={skip}
                    className="flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-white uppercase tracking-wider transition-colors"
                >
                    <SkipForward size={12} /> Overslaan
                </button>
            </div>
        </div>
    );
}
